import { Controller } from "@hotwired/stimulus"

// Менеджер фото в админке:
// - drag-and-drop загрузка файлов в dropzone (или клик по input);
// - сортировка миниатюр перетаскиванием;
// - кнопки-иконки «сделать главным» и «удалить».
export default class extends Controller {
  static targets = ["dropzone", "input", "grid", "fileList"]
  static values  = { reorderUrl: String, csrf: String }

  connect() {
    this.draggingItem = null
    this.refreshLabels()
  }

  // ——— Загрузка файлов ———

  openPicker() {
    this.inputTarget.click()
  }

  onDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("is-dragover")
  }

  onDragLeave(event) {
    if (event.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove("is-dragover")
    }
  }

  onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("is-dragover")
    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return

    const dataTransfer = new DataTransfer()
    Array.from(this.inputTarget.files || []).forEach((f) => dataTransfer.items.add(f))
    Array.from(files).forEach((f) => {
      if (f.type.startsWith("image/")) dataTransfer.items.add(f)
    })
    this.inputTarget.files = dataTransfer.files
    this.renderFileList()
  }

  onFileChange() {
    this.renderFileList()
  }

  renderFileList() {
    const files = Array.from(this.inputTarget.files || [])
    if (!this.hasFileListTarget) return
    if (files.length === 0) {
      this.fileListTarget.innerHTML = ""
      return
    }
    this.fileListTarget.innerHTML = files
      .map((f) => `<span class="photo-manager-pending">${escapeHtml(f.name)}</span>`)
      .join("")
  }

  // ——— Сортировка ———

  onItemDragStart(event) {
    this.draggingItem = event.currentTarget
    event.currentTarget.classList.add("is-dragging")
    event.dataTransfer.effectAllowed = "move"
    try { event.dataTransfer.setData("text/plain", event.currentTarget.dataset.attachmentId) } catch (_) {}
  }

  onItemDragOver(event) {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.draggingItem || target === this.draggingItem) return

    const rect = target.getBoundingClientRect()
    const isAfter = (event.clientX - rect.left) > rect.width / 2
    if (isAfter) {
      target.parentNode.insertBefore(this.draggingItem, target.nextSibling)
    } else {
      target.parentNode.insertBefore(this.draggingItem, target)
    }
  }

  onItemDragEnd(event) {
    event.currentTarget.classList.remove("is-dragging")
    this.draggingItem = null
    this.refreshLabels()
    this.persistOrder()
  }

  refreshLabels() {
    if (!this.hasGridTarget) return
    const items = this.gridTarget.querySelectorAll("[data-attachment-id]")
    items.forEach((el, idx) => {
      el.classList.toggle("is-cover", idx === 0)
      const badge = el.querySelector(".photo-manager-cover-badge")
      if (badge) badge.style.display = idx === 0 ? "" : "none"
    })
  }

  async persistOrder() {
    const url = this.reorderUrlValue
    if (!url) return
    const ids = Array.from(this.gridTarget.querySelectorAll("[data-attachment-id]"))
      .map((el) => el.dataset.attachmentId)

    try {
      await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfValue,
          "Accept": "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({ order: ids })
      })
    } catch (e) {
      console.error("photo-manager reorder failed", e)
    }
  }

  // ——— Действия (промоут/удаление) ———

  async promote(event) {
    event.preventDefault()
    const button = event.currentTarget
    const item = button.closest("[data-attachment-id]")
    if (!item) return

    const url = button.dataset.url
    const res = await fetch(url, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfValue, "Accept": "application/json" },
      credentials: "same-origin"
    })
    if (!res.ok) {
      console.error("promote failed", res.status)
      return
    }
    this.gridTarget.insertBefore(item, this.gridTarget.firstChild)
    this.refreshLabels()
  }

  async destroy(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (!confirm("Удалить фото?")) return
    const item = button.closest("[data-attachment-id]")
    if (!item) return

    const url = button.dataset.url
    const res = await fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this.csrfValue, "Accept": "application/json" },
      credentials: "same-origin"
    })
    if (!res.ok) {
      console.error("delete failed", res.status)
      return
    }
    item.remove()
    this.refreshLabels()
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}
